import { categoryTitle, type Library } from "./api.ts";
import { ChevronRight, Lock } from "./icons.tsx";
import { DIGITS } from "./navigation.ts";
import { messageCount } from "./phrases.ts";
import { Button, Card, DigitCircle, Pill, Row, RowLink } from "./ui.tsx";

interface Props {
  library: Library;
  onOpen: (digit: string, category: string) => void;
  onSetUp: (digit: string) => void;
}

/**
 * The phone's dial plan: what each digit plays, in dialing order. A topic with
 * no digit is not listed, because there is no longer a way to make one — see
 * IMPLEMENTATION.md 10.1.
 */
export function HomeScreen({ library, onOpen, onSetUp }: Props) {
  return (
    <Card class="card-rows">
      {DIGITS.map((digit) => {
        const category = library.digits[digit];
        if (category === undefined) {
          return (
            <Row
              key={digit}
              leading={<DigitCircle digit={digit} size="md" />}
              title={<span class="muted">Not in service</span>}
              trailing={
                <Button onClick={() => onSetUp(digit)}>
                  <span>Set up</span>
                </Button>
              }
            />
          );
        }
        // A digit pointing at a category with no manifest is still drawn, so
        // a broken assignment is visible rather than silently blank.
        const manifest = library.categories.find((entry) => {
          return entry.category === category;
        });
        const title = categoryTitle(library, category);
        const count = messageCount(manifest?.messages.length ?? 0);
        return (
          <RowLink
            key={digit}
            label={`${digit}: ${title}`}
            leading={<DigitCircle digit={digit} size="md" />}
            title={title}
            meta={
              manifest?.enabled === false ? (
                <>
                  <span>{count}</span>
                  <Pill tone="disabled" />
                </>
              ) : (
                count
              )
            }
            trailing={<ChevronRight />}
            onClick={() => onOpen(digit, category)}
          />
        );
      })}
      <Row
        leading={<DigitCircle digit="0" size="md" />}
        title="Directory"
        meta="Updates automatically"
        trailing={
          <span
            class="locked"
            title="The booth writes this one from your other numbers"
          >
            <Lock />
          </span>
        }
      />
    </Card>
  );
}
